"use client";

import { EMPTY_ARRAY } from "@/lib/utils/stable-empty";
import React, { useState, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { FilterContextValue, FilterContextProviderProps, AdvancedFilterGroup } from "./types";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { createFilterActions } from "./actions";
import { createInitialFiltersState, initialUiState } from "./state";
import { parseViewFilters } from "./view-filters";
import {
  FilterStableContext,
  FilterStateContext,
  type FilterStableValue,
  type FilterStateValue,
} from "./contexts";
import {
  normalizeSerializedFilters,
  convertToGraphQLFilter,
  constructSearchFilter,
  convertAdvancedFiltersToGraphQL,
} from "./utilities";

/**
 * FilterContextProvider - Provides unified filter state management
 *
 * Publishes TWO contexts (see `contexts.ts`): a STABLE one carrying
 * `{ actions, helpers }` and a VOLATILE one carrying
 * `{ filters, uiState, computed }`. A consumer that only dispatches (every
 * table row) subscribes to the stable half and is untouched by typing in the
 * search box. `useFilterContext()` composes both for call sites that read state.
 */
export function FilterContextProvider<T extends BaseEntity>({
  children,
  config,
  entityName,
  onGraphQLFilterChange,
  allViews = EMPTY_ARRAY,
}: FilterContextProviderProps<T>) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Memoize the view ID to avoid unnecessary effect runs
  const viewId = useMemo(() => searchParams?.get("view") || null, [searchParams]);

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [filters, setFilters] = useState<FilterContextValue["filters"]>(() => {
    if (viewId) {
      const configView = config.savedViews?.find((v) => v.id === viewId);
      if (configView) {
        // Config views are synchronously available — apply filters immediately
        // to prevent the initial unfiltered data flash.
        return parseViewFilters(
          configView as { filters?: Record<string, unknown> },
          viewId
        );
      }
      // Supabase-only view: start with null activeViewId so the URL-sync effect
      // fires and applies filters once allViews loads asynchronously.
      return createInitialFiltersState(null);
    }
    return createInitialFiltersState(null);
  });

  const [uiState, setUiState] = useState<FilterContextValue["uiState"]>(() => ({
    ...initialUiState,
    isOpen: config.features?.filters === true,
  }));

  // Debounced search
  const {
    searchValue: searchTerm,
    debouncedSearchValue: debouncedSearchTerm,
    setSearchValue: setSearchTermRaw,
  } = useDebouncedSearch({
    initialValue: filters.search,
    delay: 300,
  });

  // ============================================================================
  // SYNC URL VIEW PARAM TO FILTERS
  // ============================================================================
  // Handles ONLY external URL changes (browser back/forward) and async Supabase
  // view loads. `applyPreset` writes both filter state AND the URL itself, so it
  // is the authoritative writer for user-initiated tab clicks.
  //
  // CRITICAL: `router.push` updates `useSearchParams` asynchronously, so right
  // after `applyPreset` runs, `filters.activeViewId` leads `viewId` (the URL has
  // not caught up yet). This effect depends on `filters.activeViewId`, so it
  // re-runs in that window with a STALE `viewId`. Without the guard below it would
  // clobber the just-applied filters (reset to empty, or re-apply the previous
  // view). We therefore act only when the URL `viewId` or `allViews` actually
  // changed — never on `applyPreset`'s own `activeViewId` write.
  const prevViewIdRef = React.useRef(viewId);
  const prevAllViewsRef = React.useRef(allViews);
  React.useEffect(() => {
    const urlViewChanged = prevViewIdRef.current !== viewId;
    const allViewsChanged = prevAllViewsRef.current !== allViews;
    prevViewIdRef.current = viewId;
    prevAllViewsRef.current = allViews;

    // Re-run triggered solely by applyPreset moving activeViewId ahead of the
    // pending router.push — state is already correct, do not fight it.
    if (!urlViewChanged && !allViewsChanged) return;

    if (viewId && viewId !== filters.activeViewId) {
      const savedView =
        allViews?.find((v) => v.id === viewId) ??
        config.savedViews?.find((v) => v.id === viewId);

      if (savedView) {
        const newFilters = parseViewFilters(
          savedView as { filters?: Record<string, unknown> },
          viewId
        );
        setFilters(newFilters);
        setSearchTermRaw(newFilters.search);
      }
      // If not found yet, allViews hasn't loaded — the dep on allViews ensures
      // this effect re-runs when Supabase data arrives.
    } else if (!viewId && filters.activeViewId) {
      setFilters(createInitialFiltersState(null));
      setSearchTermRaw("");
    }
  }, [viewId, filters.activeViewId, allViews, config.savedViews]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const actions = createFilterActions(
    setFilters,
    setUiState,
    setSearchTermRaw,
    config,
    router,
    allViews
  );

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // NOTE: customFilterHooks (e.g. useCompaniesWithNoTicketsThisMonth) are data-fetching
  // hooks that return { data, loading, error, refetch } — NOT GraphQL filter criteria.
  // They are handled by useActiveCustomFilter in the UniversalEntityPage data-fetching
  // layer and must NOT be called here. Calling them conditionally would also violate
  // React's Rules of Hooks.
  //
  // FilterContext only produces filter criteria (simple/advanced/search).
  // See: useActiveCustomFilter.ts, useDataFetching.ts

  const graphqlFilter = useMemo(() => {
    let filter: Record<string, unknown> = {};

    // Convert simple filters to GraphQL format
    if (Object.keys(filters.simple).length > 0) {
      const normalizedFilters = normalizeSerializedFilters(filters.simple);
      const convertedFilters = convertToGraphQLFilter(normalizedFilters, config, entityName);
      filter = { ...convertedFilters };
    }

    // Merge advanced filters (GraphQL "where" syntax)
    if (filters.advanced.length > 0) {
      const advancedGraphQL = convertAdvancedFiltersToGraphQL(filters.advanced);
      filter = { ...filter, ...advancedGraphQL };
    }

    // Add search filter (converted to GraphQL in useQueryParams)
    if (debouncedSearchTerm) {
      filter._search = debouncedSearchTerm;
    }

    return filter;
  }, [
    filters.simple,
    filters.advanced,
    debouncedSearchTerm,
    config,
    entityName,
  ]);

  const isActive = useMemo(() => {
    return (
      Object.keys(filters.simple).length > 0 ||
      filters.advanced.length > 0 ||
      filters.search.length > 0
    );
  }, [filters.simple, filters.advanced, filters.search]);

  const filterCount = useMemo(() => {
    let count = Object.keys(filters.simple).length + filters.advanced.length;
    if (filters.search) count += 1;
    return count;
  }, [filters.simple, filters.advanced, filters.search]);

  // ============================================================================
  // FILTER HELPER FUNCTIONS
  // ============================================================================

  // Helper function to convert simple filters to GraphQL filter format
  const convertToGraphQLFilterHelper = React.useCallback(
    (filtersToConvert: Record<string, unknown>) => {
      const normalizedFilters = normalizeSerializedFilters(filtersToConvert);
      return convertToGraphQLFilter(normalizedFilters, config, entityName);
    },
    [entityName, config]
  );

  // Helper function to construct search filter
  const constructSearchFilterHelper = React.useCallback(
    (searchTermValue: string) => {
      return constructSearchFilter(searchTermValue, config, entityName);
    },
    [config, entityName]
  );

  // Helper function to convert advanced filters to GraphQL
  const convertAdvancedFiltersToGraphQLHelper = React.useCallback(
    (advancedFilters: AdvancedFilterGroup[] | Record<string, unknown>) => {
      return convertAdvancedFiltersToGraphQL(advancedFilters);
    },
    []
  );

  // Notify parent when GraphQL filter changes
  React.useEffect(() => {
    onGraphQLFilterChange?.(graphqlFilter);
  }, [graphqlFilter, onGraphQLFilterChange]);

  // ============================================================================
  // CONTEXT VALUES
  // ============================================================================

  // Stable half — identity survives every keystroke, so dispatch-only consumers
  // (e.g. MemoizedTableRow) never re-render because someone typed.
  const stableValue: FilterStableValue = useMemo(
    () => ({
      actions,
      helpers: {
        convertToGraphQLFilter: convertToGraphQLFilterHelper,
        constructSearchFilter: constructSearchFilterHelper,
        convertAdvancedFiltersToGraphQL: convertAdvancedFiltersToGraphQLHelper,
      },
    }),
    [
      actions,
      convertToGraphQLFilterHelper,
      constructSearchFilterHelper,
      convertAdvancedFiltersToGraphQLHelper,
    ]
  );

  // Volatile half — changes whenever filter state, UI state, or the derived
  // GraphQL filter changes.
  const stateValue: FilterStateValue = useMemo(
    () => ({
      filters,
      uiState,
      computed: { graphqlFilter, isActive, filterCount },
    }),
    [filters, uiState, graphqlFilter, isActive, filterCount]
  );

  return (
    <FilterStableContext.Provider value={stableValue}>
      <FilterStateContext.Provider value={stateValue}>{children}</FilterStateContext.Provider>
    </FilterStableContext.Provider>
  );
}
